import React, { useMemo, useState } from "react";

import { Check, ChevronsUpDown, X } from "lucide-react";

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "js/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "js/components/ui/popover";
import { cn } from "lib/utils";

interface FilterOption {
  label: string;
  value: string;
}

interface FilterChipProps {
  label: string;
  value?: string;
  options: FilterOption[];
  onChange: (value?: string) => void;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [value, options],
  );

  const handleSelect = (nextValue: string | undefined) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
              "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
              value && "border-[#0060ff]/50 bg-[#0060ff]/10 text-[#0043ad]",
            )}
          >
            <span className="font-medium text-slate-700">{label}</span>
            <span className="text-xs font-medium text-slate-400">
              {selectedOption?.label ?? "Todos"}
            </span>
            <ChevronsUpDown className="size-3.5 text-slate-400" />
          </button>
        </PopoverTrigger>
        {value && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSelect(undefined);
            }}
            className="absolute -right-2 -top-1.5 flex size-5 items-center justify-center rounded-full border border-white bg-white text-slate-400 shadow-sm transition hover:text-slate-600"
          >
            <X className="size-3" />
            <span className="sr-only">Limpiar {label}</span>
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Buscar ${label.toLowerCase()}`} />
          <CommandList>
            <CommandEmpty>Sin coincidencias</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all"
                onSelect={() => handleSelect(undefined)}
                className="cursor-pointer"
              >
                <Check className={cn("mr-2 size-4", !value ? "opacity-100" : "opacity-0")} />
                Todos
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(current) => handleSelect(current)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default FilterChip;
